import {TIMER_DEFAULT_MINUTES} from "./timer.state";

/**
 * The gimmicks a tile can carry (feature plans 0071, 0072, 0073). Domain side only — names, labels, looks, default
 * options — read by `link-target-serializer.ts` (which names are valid), the tile dialog (what to offer), and the
 * viewer's look (`gimmick-skin.store.ts`, `GimmickSkinController`).
 * How a gimmick renders lives on the UI side (`components/dashboard-creator/tiles/gimmick-renderers.ts`,
 * `GIMMICK_RENDERERS`), the same domain/UI split as `tile-registry.ts`.
 *
 * Adding a gimmick: its looks in `SKINS` (first = default), a definition in `GIMMICKS` with its label key, a
 * renderer in `gimmick-renderers.ts`. The look types are derived from `SKINS`, so a renderer map keyed by
 * `SkinOf<…>` fails to compile until every look has its renderer.
 */

/** The four clock looks, shared by the stopwatch and the timer (feature plan 0073). */
export const CLOCK_SKINS = ['ring', 'chrono', 'led', 'flap'] as const;
export type ClockSkin = typeof CLOCK_SKINS[number];

/** The looks of each gimmick, in the order the tile's "Optik wechseln" button cycles through them; the first is
 *  the default. */
const SKINS = {
    stopwatch: CLOCK_SKINS,
    picker: ['cards', 'slot', 'drum', 'wheel'],
    timer: CLOCK_SKINS,
} as const;

export type GimmickName = keyof typeof SKINS;
export type SkinOf<N extends GimmickName> = typeof SKINS[N][number];

export interface GimmickDefinition {
    readonly name: GimmickName;
    /** de.json key of the gimmick's name. */
    readonly labelKey: string;
    /** The options a freshly chosen gimmick starts with — `skin` is its default look; the timer adds its preset
     *  `minutes`. */
    readonly defaultOptions: Readonly<Record<string, unknown>>;
}

export const GIMMICKS: readonly GimmickDefinition[] = [
    {name: 'stopwatch', labelKey: 'gimmicks.names.stopwatch', defaultOptions: {skin: defaultSkinOf('stopwatch')}},
    {name: 'picker', labelKey: 'gimmicks.names.picker', defaultOptions: {skin: defaultSkinOf('picker')}},
    {
        name: 'timer',
        labelKey: 'gimmicks.names.timer',
        defaultOptions: {skin: defaultSkinOf('timer'), minutes: TIMER_DEFAULT_MINUTES},
    },
];

export const GIMMICK_NAMES: readonly GimmickName[] = GIMMICKS.map((gimmick) => gimmick.name);

export function isGimmickName(value: unknown): value is GimmickName {
    return typeof value === 'string' && (GIMMICK_NAMES as readonly string[]).includes(value);
}

/** The definition of a known gimmick. */
export function gimmickDefinition(name: GimmickName): GimmickDefinition {
    return GIMMICKS.find((gimmick) => gimmick.name === name)!;
}

/** The looks of a gimmick, in button order. */
export function skinsOf<N extends GimmickName>(name: N): readonly SkinOf<N>[] {
    return SKINS[name] as readonly SkinOf<N>[];
}

/** The default look of a gimmick (the first of its looks). */
export function defaultSkinOf<N extends GimmickName>(name: N): SkinOf<N> {
    return SKINS[name][0] as SkinOf<N>;
}

export function isSkinOf<N extends GimmickName>(name: N, value: unknown): value is SkinOf<N> {
    return typeof value === 'string' && (skinsOf(name) as readonly string[]).includes(value);
}

/** A stored look as the gimmick shows it: an unknown or missing value falls back to the default. The stored raw
 *  value itself is left alone (it may come from a newer version). */
export function resolveSkin<N extends GimmickName>(name: N, raw: unknown): SkinOf<N> {
    return isSkinOf(name, raw) ? raw : defaultSkinOf(name);
}

/** The look after `skin`, wrapping around — the order of the tile's button. */
export function nextSkin<N extends GimmickName>(name: N, skin: SkinOf<N>): SkinOf<N> {
    const skins = skinsOf(name);
    return skins[(skins.indexOf(skin) + 1) % skins.length];
}
